import Vue from 'vue';
import { Component, Prop, Watch } from 'vue-property-decorator';
import { uiDebounce } from 'src/util/debounce';
import scrollIntoView from 'src/util/scroll-into-view';

export interface ColumnInfo {
	name: string;
	cols: number;
	visibility: 'Always' | 'Never' | 'When';
}

/**
 * Gets the percentage width to use for a column
 * @param cols The number of cols that the column is alloted
 * @param totalCols The total number of cols in the table
 * @param scale The percentage scale that the table is currently at (i.e. 1.0=full width, 0.5=half width, etc.)
 */
export function getColumnWidth(cols: number, totalCols: number, scale: number = 1): number {
	const baseWidth = cols / totalCols;
	const minWidth = baseWidth * Math.pow(1 - baseWidth, 2);
	return baseWidth * (minWidth / (baseWidth * scale) - 1);
}

@Component
export default class ResponsiveTable extends Vue {
	@Prop()
	columns: ColumnInfo[];

	@Prop({ default: 24 })
	cols: number;

	@Prop({ default: 24 })
	parentCols: number;

	@Prop()
	width: number;

	// A map of positive or negative adjustments for column widths
	columnAdjustments: { [name: string]: number } = {};

	// Call the resize handler no more than once per X milliseconds
	debouncedHandleResize = uiDebounce(() => this.handleResize());

	// The pct width amount that columns are able to grow
	grow: number = 0;

	// The pct width amount that columns are able to grow (if all columns are visible)
	baselineGrow: number = 0;

	// The pct width amount that columns must shrink by
	shrink: number = 0;

	// The pct width amount that columns must shrink by (if all columns are visible)
	baselineShrink: number = 0;

	get isTable(): boolean {
		// Revert to non-table mode if the table is too cramped (or could become too cramped if conditionally visible columns were shown)
		return (this.baselineGrow === 0 && this.baselineShrink === 0) || this.baselineGrow < this.baselineShrink;
	}

	get columnVisibility(): { [name: string]: boolean } {
		// By default all columns are visible, the computed must be overriden in order to identify visible vs. hidden columns
		return this.columns.reduce((map, c) => {
			map[c.name] = true;
			return map;
		}, {});
	}

	get columnWidths(): { [name: string]: number } {
		const grow = this.grow;
		const shrink = this.shrink;
		const totalVisibleCols = this.visibleColumns.reduce((cols, c) => cols + c.cols, 0);
		return this.columns.reduce((map, c) => {
			// The base width is simply derived from the ratio of cols
			let width: number = c.cols / totalVisibleCols;
			// If the column is visible then adjust the width, otherwise zero it out
			const isVisible = this.columnVisibility[c.name];
			if (isVisible) {
				const adjustment = this.columnAdjustments[c.name];
				if (adjustment > 0) {
					width += adjustment;
				}
				else {
					const shrinkFactor = shrink === 0 ? 1 : grow / shrink;
					width -= (-adjustment) * shrinkFactor;
				}
			}
			else {
				// Set the column width to 0 to reflect that it is not visible
				width = 0;
			}
			map[c.name] = width;
			return map;
		}, {});
	}

	get visibleColumns() {
		return this.columns.filter(c => this.columnVisibility[c.name]);
	}

	get hiddenColumns() {
		return this.columns.filter(c => !this.columnVisibility[c.name]);
	}

	get paddingCols() {
		return this.cols - this.columns.reduce((cols, c) => cols + c.cols, 0);
	}

	@Watch('columns')
	handleColumnsChanged() {
		// If columns are changed, zero out adjustments (they will be recomputed)
		this.columnAdjustments = this.columns.reduce((obj, column) => { obj[column.name] = 0; return obj; }, {});
		// Resize columns when the list of columns changes
		this.debouncedHandleResize();
	}

	@Watch('visibleColumns')
	handleVisibleColumnsChanged() {
		// Resize columns when the list of visible columns changes
		this.debouncedHandleResize();
	}

	@Watch('isTable')
	maintainInputFocus(isTable: boolean, wasTable: boolean) {
		if (document.activeElement) {
			const childFields = Array.from(this.$el.querySelectorAll('textarea, input, select'));
			const focusedIndex = childFields.indexOf(document.activeElement);

			if (focusedIndex > -1) {
				// Wait for the table to rerender as a repeating section
				requestAnimationFrame(() => {
					const activeField = this.$el.querySelectorAll('textarea, input, select')[focusedIndex] as HTMLInputElement;

					if (activeField) {
						activeField.focus();
						scrollIntoView(activeField);
					}
				});
			}
		}
	}

	created() {
		// Run the column resize logic when the window resized
		window.addEventListener('resize', this.debouncedHandleResize as ((this: Window, ev: UIEvent) => void));
	}

	mounted() {
		// Run the column resizing logic initially (to adjust to initial window size)
		this.debouncedHandleResize();
	}

	destroyed() {
		// Remove the resize handler when the component is destroyed
		window.removeEventListener('resize', this.debouncedHandleResize as ((this: Window, ev: UIEvent) => void));
	}

	handleResize() {
		requestAnimationFrame(() => {
			const columns = this.columns;

			// Determine the total number of cols in play based on what columns are visible
			const totalCols = columns.reduce((cols, c) => cols + c.cols, 0) + this.paddingCols;
			const alwaysHiddenCols = columns.filter(c => c.visibility === 'Never').reduce((cols, c) => cols + c.cols, 0);
			const hiddenCols = this.hiddenColumns.reduce((cols, c) => cols + c.cols, 0);
			const totalVisibleCols = totalCols - hiddenCols;
			const maxTotalVisibleCols = totalCols - alwaysHiddenCols;

			// Determine the maximum size that the table can be if the window's size doesn't restrict it
			const tableWidthRatio = this.cols / this.parentCols;
			const maxTableWidth = 800 * tableWidthRatio;

			// Get the available width: client width up to 800px
			const availableWidth = Math.min(this.width || this.$el.parentElement.clientWidth, 800);
			const actualTableWidth = availableWidth * tableWidthRatio;

			// Determine to what degree the table width is scaled down do to constrained available space
			const scale = actualTableWidth / maxTableWidth;

			// Determine the necessary column width adjustments
			let grow = 0;
			let baselineGrow = 0;
			let shrink = 0;
			let baselineShrink = 0;
			for (const column of columns) {
				const isVisible = this.columnVisibility[column.name];
				if (!isVisible) {
					this.columnAdjustments[column.name] = 0;
				}
				else {
					const adjustment = getColumnWidth(column.cols, totalVisibleCols, scale);
					this.columnAdjustments[column.name] = adjustment;
					if (adjustment > 0)
						grow += adjustment;
					else
						shrink += -adjustment;
				}

				// Calculate a baseline adjustment for always and conditionally visible columns so that
				// responsive table/section state can be determined based on all columns that could be visible
				if (column.visibility !== 'Never') {
					const baselineAdjustment = getColumnWidth(column.cols, maxTotalVisibleCols, scale);
					if (baselineAdjustment > 0)
						baselineGrow += baselineAdjustment;
					else
						baselineShrink += -baselineAdjustment;
				}
			}

			// Update grow and shrink
			this.grow = grow;
			this.baselineGrow = baselineGrow;
			this.shrink = shrink;
			this.baselineShrink = baselineShrink;
		});
	}
}
